import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '@/components/ui/Button';
import { Coin3D, Chest3D, StarXP } from '@/components/ui/GameIcons';

/* Moedas que voam do baú até ao contador de moedas do HUD (#hud-coins). */
function FlyingCoin({ delay, target }) {
  const startX = window.innerWidth / 2 + (Math.random() * 120 - 60);
  const startY = window.innerHeight / 2 - 40 + (Math.random() * 60 - 30);
  return (
    <motion.div
      className="pointer-events-none fixed z-[60]"
      style={{ left: 0, top: 0 }}
      initial={{ x: startX, y: startY, scale: 0.4, opacity: 0 }}
      animate={{
        x: [startX, startX + (Math.random() * 80 - 40), target.x],
        y: [startY, startY - 90 - Math.random() * 50, target.y],
        scale: [0.4, 1.15, 0.55],
        opacity: [0, 1, 1],
      }}
      transition={{ duration: 0.9, delay, ease: 'easeIn', times: [0, 0.45, 1] }}
    >
      <Coin3D className="h-8 w-8 drop-shadow-lg" />
    </motion.div>
  );
}

function useCountUp(value, duration = 900, delay = 400) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now() + delay;
    const tick = (now) => {
      const t = Math.min(1, Math.max(0, (now - start) / duration));
      setDisplay(Math.round(value * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration, delay]);
  return display;
}

export default function RewardModal({ data, onClose }) {
  const coins = data?.rewards?.coins || 0;
  const xp = data?.rewards?.xp || 0;
  const coinsShown = useCountUp(coins);
  const xpShown = useCountUp(xp);

  // Destino do voo das moedas: o contador de moedas do HUD.
  const coinTarget = useMemo(() => {
    const el = document.getElementById('hud-coins');
    if (el) {
      const rect = el.getBoundingClientRect();
      return { x: rect.left + rect.width / 2 - 16, y: rect.top + rect.height / 2 - 16 };
    }
    return { x: 80, y: 40 };
  }, [data]);

  if (!data) return null;

  const flyingCoins = Array.from({ length: Math.min(10, Math.max(5, Math.round(coins / 10))) }, (_, i) => i * 0.09 + 0.35);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'radial-gradient(circle, rgb(var(--hc-accent) / 0.28) 0%, rgb(var(--hc-bg) / 0.94) 100%)' }}
      >
        {flyingCoins.map((delay, index) => (
          <FlyingCoin key={index} delay={delay} target={coinTarget} />
        ))}

        <motion.div
          initial={{ scale: 0.5, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ type: 'spring', damping: 15, stiffness: 300 }}
          className="parchment-card relative w-full max-w-sm p-5"
        >
          <div className="mb-4 text-center">
            <motion.div
              className="mx-auto mb-1 flex justify-center"
              initial={{ scale: 0, rotate: -12 }}
              animate={{ scale: [0, 1.25, 1], rotate: [-12, 4, 0] }}
              transition={{ duration: 0.55, delay: 0.15 }}
            >
              <Chest3D className="h-24 w-24 drop-shadow-xl" />
            </motion.div>
            <h2 className="font-urban text-xl font-black text-fg">Tesouro coletado!</h2>
            <p className="mt-1 text-sm font-bold text-fg/70">{data?.poi?.name}</p>
          </div>

          <div className="mb-5 flex justify-center gap-4">
            <div className="hc-surface flex-1 px-3 py-3 text-center">
              <Coin3D className="mx-auto h-10 w-10" />
              <div className="mt-1 font-urban text-2xl font-black text-accent tabular-nums">+{coinsShown}</div>
              <div className="text-xs font-bold text-muted">Moedas</div>
            </div>
            <div className="hc-surface flex-1 px-3 py-3 text-center">
              <StarXP className="mx-auto h-10 w-10" />
              <div className="mt-1 font-urban text-2xl font-black text-xp tabular-nums">+{xpShown}</div>
              <div className="text-xs font-bold text-muted">XP</div>
            </div>
          </div>

          {data?.rewards?.isFirstVisit && (
            <div className="mb-4 rounded-xl border border-forest/40 bg-forest-light/15 py-2 text-center text-xs font-black text-forest-light">
              🌟 Primeira visita — bónus aplicado!
            </div>
          )}

          {data?.leveledUp && (
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.6, type: 'spring', stiffness: 300 }}
              className="mb-4 rounded-xl border border-accent/40 bg-accent/15 py-2 text-center text-sm font-black text-accent"
            >
              🎉 Subiste para o nível {data.newLevel}!
            </motion.div>
          )}

          <Button variant="green" fullWidth onClick={onClose}>
            Continuar a explorar
          </Button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
